import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

// Ícones ultra-avançados
const ARIcon = () => (<svg width="20" height="20" fill="currentColor"><circle cx="10" cy="10" r="9"/><text x="5" y="14" fontSize="8" fill="#fff">AR</text></svg>);
const ChallengeIcon = () => (<svg width="20" height="20" fill="currentColor"><rect x="2" y="2" width="16" height="16" rx="3"/><text x="4" y="14" fontSize="8" fill="#fff">🏆</text></svg>);
const LiveIcon = () => (<svg width="20" height="20" fill="red"><circle cx="10" cy="10" r="9"/><text x="3" y="14" fontSize="7" fill="#fff">LIVE</text></svg>);
const PrivacyIcon = () => (<svg width="20" height="20" fill="currentColor"><rect x="3" y="6" width="14" height="9" rx="2"/><circle cx="10" cy="10" r="2" fill="#fff"/></svg>);
const NotificationIcon = () => (<svg width="20" height="20" fill="currentColor"><path d="M10 18c.83 0 1.5-.67 1.5-1.5h-3c0 .83.67 1.5 1.5 1.5zm4.5-4.5V9c0-2.3-1.22-4.23-3.38-4.74V4c0-.62-.5-1.12-1.12-1.12-.62 0-1.12.5-1.12 1.12v.26C6.72 4.77 5.5 6.7 5.5 9v4.5L4.12 14.88c-.1.1-.22.25-.22.45V16h12.2v-.67c0-.2-.12-.35-.22-.45L14.5 13.5z"/></svg>);
const ModerationIcon = () => (<svg width="20" height="20" fill="orange"><rect x="2" y="2" width="16" height="16" rx="2"/><text x="3" y="14" fontSize="7" fill="#fff">MOD</text></svg>);
const APIIcon = () => (<svg width="20" height="20" fill="blue"><rect x="2" y="2" width="16" height="16" rx="2"/><text x="4" y="14" fontSize="7" fill="#fff">API</text></svg>);

export default function SpotifyFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => setEditingPost(post);
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    if (currentPlaying?._id === postId) {
      setCurrentPlaying(null);
      setIsPlaying(false);
    }
  };

  // Handlers ultra-avançados
  const handleAR = (post) => alert("Aplicar efeitos AR nesta música");
  const handleChallenge = (post) => alert("Participar de desafio musical");
  const handleLiveStream = (post) => alert("Iniciar live com esta música");
  const handlePrivacy = (post) => alert("Configurar privacidade");
  const handleNotifications = (post) => alert("Gerenciar notificações");
  const handleModeration = (post) => alert("Ver moderação");
  const handleAPIIntegration = (post) => alert("Integrar com APIs externas");

  const categories = [
    { id: "all", name: "Todos" },
    { id: "track", name: "Músicas" },
    { id: "album", name: "Álbuns" },
    { id: "playlist", name: "Playlists" },
    { id: "artist", name: "Artistas" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/spotify?page=${pageNum}&limit=20`;
      if (category !== "all") url += `&category=${category}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching spotify feed:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const playTrack = (post) => {
    if (currentPlaying?._id === post._id) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      setCurrentPlaying(post);
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (currentPlaying && audioRef.current) {
      audioRef.current.src = currentPlaying.spotifyData?.previewUrl || currentPlaying.images?.[0];
      audioRef.current.play().catch(() => {});
    }
  }, [currentPlaying]);

  if (loading && posts.length === 0) {
    return <div className="spotify_feed_loading"><div className="spotify_loader"></div><span>Carregando músicas...</span></div>;
  }

  return (
    <div className="spotify_feed">
      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="spotify_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
          <h3>Nenhuma música encontrada</h3>
          <p>Seja o primeiro a compartilhar uma música!</p>
        </div>
      ) : (
        <div className="spotify_grid">
          {posts.map((post) => (
            <div key={post._id} className={`spotify_card ${currentPlaying?._id === post._id ? "playing" : ""}`}>
              <div className="spotify_cover" onClick={() => playTrack(post)}>
                <img src={post.spotifyData?.coverImage || post.images?.[0]?.url || post.images?.[0] || "/images/default-album.png"} alt="" />
                <div className="spotify_play_overlay">
                  {currentPlaying?._id === post._id && isPlaying ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </div>
              </div>
              <div className="spotify_card_content">
                <h3>{post.spotifyData?.trackName || post.spotifyData?.title || post.text}</h3>
                <p className="spotify_artist">{post.spotifyData?.artistName || "Artista desconhecido"}</p>
                {post.spotifyData?.albumName && (
                  <p className="spotify_album">{post.spotifyData.albumName}</p>
                )}
                <Link to={`/profile/${post.user.username}`} className="spotify_sharer">
                  <img src={post.user.picture} alt="" />
                  <span>{post.user.first_name}</span>
                </Link>
                <div onClick={(e) => e.stopPropagation()}>
                  <FeedPostMenu
                    post={post}
                    onEdit={handleEditPost}
                    onPostDeleted={handlePostDeleted}
                  />
                </div>

                {/* Botões Ultra-Avançados */}
                <div className="spotify_advanced_actions" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => handleAR(post)} title="AR" className="spotify_action_btn">
                    <ARIcon />
                  </button>
                  <button onClick={() => handleChallenge(post)} title="Desafio" className="spotify_action_btn">
                    <ChallengeIcon />
                  </button>
                  <button onClick={() => handleLiveStream(post)} title="Live" className="spotify_action_btn">
                    <LiveIcon />
                  </button>
                  <button onClick={() => handlePrivacy(post)} title="Privacidade" className="spotify_action_btn">
                    <PrivacyIcon />
                  </button>
                  <button onClick={() => handleNotifications(post)} title="Notificações" className="spotify_action_btn">
                    <NotificationIcon />
                  </button>
                  <button onClick={() => handleModeration(post)} title="Moderação" className="spotify_action_btn">
                    <ModerationIcon />
                  </button>
                  <button onClick={() => handleAPIIntegration(post)} title="API" className="spotify_action_btn">
                    <APIIcon />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="spotify_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} style={{ display: "none" }} />

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
